import { useLocale, useTranslations } from "next-intl";

import { localisedPath } from "../../../lib/i18n/alternates";
import { toLocale } from "../../../lib/i18n/localeCookie";
import { LanguageSwitcher } from "../../i18n/LanguageSwitcher";
import tokens from "../styles/tokens";

const socialLinks = [
  {
    src: "/static/reflect/icons/discordIcon.svg",
    altKey: "discord",
    href: "https://discord.com/invite/T85RwZuW6b",
  },
  {
    src: "/static/reflect/icons/tiktokIcon.svg",
    altKey: "tiktok",
    href: "https://www.tiktok.com/@psy_diaries",
  },
];

/** Style of the two legal links; the catalog string supplies their text. */
const legalLinkStyle = {
  color: tokens.colors.neutral[0],
  textDecoration: "underline",
};

/** The copyright, legal and contact lines' shared type. */
const smallPrintStyle = {
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: 1.4,
  color: tokens.colors.neutral[0],
  letterSpacing: "0.06px",
};

/** The spacing above each small-print line after the first. */
const smallPrintGap = `${tokens.spacing.s} 0 0 0`;

/** The homepage footer; copy from `web.home.footer`. */
const WaitlistFooter = () => {
  const t = useTranslations("web.home.footer");
  // The legal links stay in the page's language: `/es/` links `/es/privacy`.
  const locale = toLocale(useLocale());
  return (
    <div
      style={{
        backgroundColor: tokens.colors.brand[700],
        padding: `${tokens.spacing["2xl"]} ${tokens.spacing.l} ${tokens.spacing["3xl"]}`,
        textAlign: "center",
      }}
    >
      <div
        style={{
          maxWidth: "343px",
          margin: "0 auto",
        }}
      >
        {/* Social */}
        <div style={{ marginBottom: tokens.spacing["2xl"] }}>
          <p
            style={{
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: 1.5,
              color: tokens.colors.neutral[0],
              marginBottom: tokens.spacing.m,
            }}
          >
            {t("stayConnected")}
          </p>
          <div
            style={{
              display: "flex",
              gap: tokens.spacing.xl,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {socialLinks.map((social) => (
              <a
                key={social.href}
                href={social.href}
                data-test-id={`footer-social-${social.altKey}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <img
                  src={social.src}
                  alt={t(social.altKey)}
                  loading="lazy"
                  style={{
                    width: "24px",
                    height: "24px",
                  }}
                />
              </a>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <p style={{ ...smallPrintStyle, margin: 0 }}>{t("copyright")}</p>

        {/* Legal links */}
        <p style={{ ...smallPrintStyle, margin: smallPrintGap }}>
          {t.rich("legal", {
            privacy: (chunks) => (
              <a
                href={localisedPath("/privacy", locale)}
                data-test-id="footer-privacy"
                style={legalLinkStyle}
              >
                {chunks}
              </a>
            ),
            terms: (chunks) => (
              <a
                href={localisedPath("/terms", locale)}
                data-test-id="footer-terms"
                style={legalLinkStyle}
              >
                {chunks}
              </a>
            ),
          })}
        </p>

        {/* Contact */}
        <p style={{ ...smallPrintStyle, margin: smallPrintGap }}>{t("contact")}</p>

        {/* Renders nothing while only one locale is live. */}
        <LanguageSwitcher className="mt-s text-xs text-neutral-0" />
      </div>
    </div>
  );
};

export default WaitlistFooter;
